import React, { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import { api, getToken, logout } from "@/lib/api";
import { Loader2, ShieldAlert, Download, Trash2, FileText, Database, AlertTriangle, Scale, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * GDPR · self-serve compliance surface (iter 31.20).
 *
 * Plain-language policy (from /api/gdpr/policy), live inventory of every
 * collection holding the user's data (from /api/gdpr/inventory), and the
 * two controls — Export ZIP and Erase Everything (with a dramatic 5-second
 * countdown matching the Ledger danger zone).
 */
export default function Gdpr() {
    const navigate = useNavigate();
    const [policy, setPolicy] = useState(null);
    const [inventory, setInventory] = useState(null);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(true);

    // Erase flow state
    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmText, setConfirmText] = useState("");
    const [countdown, setCountdown] = useState(null);
    const [erasing, setErasing] = useState(false);
    const [eraseReport, setEraseReport] = useState(null);
    const [eraseError, setEraseError] = useState("");

    const refreshInventory = async () => {
        try {
            const { data } = await api.get("/gdpr/inventory");
            setInventory(data);
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message || "Could not load inventory");
        }
    };

    useEffect(() => {
        (async () => {
            try {
                const [{ data: p }, { data: inv }] = await Promise.all([
                    api.get("/gdpr/policy"),
                    api.get("/gdpr/inventory"),
                ]);
                setPolicy(p);
                setInventory(inv);
            } catch (e) {
                setErr(e?.response?.data?.detail || e.message || "Could not load GDPR data");
            } finally {
                setBusy(false);
            }
        })();
    }, []);

    // Export ZIP — same endpoint Ledger danger zone uses
    const exportZip = async () => {
        try {
            const token = getToken();
            const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
            const resp = await fetch(`${base}/api/data/export.zip`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!resp.ok) throw new Error(`[HTTP ${resp.status}] export failed`);
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const d = new Date().toISOString().slice(0, 10);
            a.download = `spy-finance-export-${d}.zip`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            setErr(`Export failed: ${e.message}`);
        }
    };

    // Erase — 5-second dramatic countdown then SSE call
    const beginErase = () => {
        if (confirmText !== "ERASE") return;
        setEraseError("");
        setEraseReport(null);
        setCountdown(5);
    };

    useEffect(() => {
        if (countdown === null) return;
        if (countdown <= 0) {
            (async () => {
                setErasing(true);
                try {
                    const token = getToken();
                    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
                    const resp = await fetch(`${base}/api/gdpr/erase-all`, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({ confirm: "ERASE", i_understand: true }),
                    });
                    if (!resp.ok || !resp.body) {
                        const txt = await resp.text();
                        throw new Error(`[HTTP ${resp.status}] ${txt.slice(0, 200)}`);
                    }
                    // Parse SSE stream until we hit result/error
                    const reader = resp.body.getReader();
                    const decoder = new TextDecoder();
                    let buf = "";
                    let finalFrame = null;
                    while (true) {
                        const { value, done } = await reader.read();
                        if (done) break;
                        buf += decoder.decode(value, { stream: true });
                        const frames = buf.split("\n\n");
                        buf = frames.pop() || "";
                        for (const frame of frames) {
                            const line = frame.split("\n").find((l) => l.startsWith("data:"));
                            if (!line) continue;
                            try {
                                const parsed = JSON.parse(line.slice(5).trim());
                                if (parsed.type === "result" || parsed.type === "error") {
                                    finalFrame = parsed;
                                }
                            } catch (_) { /* heartbeat */ }
                        }
                    }
                    if (!finalFrame) throw new Error("No final frame from erase stream");
                    if (finalFrame.type === "error") throw new Error(finalFrame.detail || "Erase failed");
                    setEraseReport(finalFrame);
                    // The user record is gone — sign out + return to login after a beat.
                    setTimeout(() => {
                        try { logout(); } catch (_) { /* ignore */ }
                        navigate("/login?erased=1");
                    }, 4000);
                } catch (e) {
                    setEraseError(e.message);
                } finally {
                    setErasing(false);
                    setCountdown(null);
                }
            })();
            return;
        }
        const t = setTimeout(() => setCountdown((c) => (c === null ? null : c - 1)), 1000);
        return () => clearTimeout(t);
    }, [countdown, navigate]);

    return (
        <Shell>
            <div className="mb-8">
                <div className="label-typed tw-prefix">Compliance · self-serve · your data</div>
                <h1 className="font-heading text-5xl uppercase tracking-[0.04em] text-ink mt-1 flex items-center gap-3">
                    <Scale className="w-10 h-10 text-stamp-red" strokeWidth={1.5} />
                    GDPR<span className="cursor-blink" />
                </h1>
                <p className="font-body text-sm text-ink-soft mt-3 max-w-2xl">
                    Your data, your rights. Plain language, live inventory, two controls — download
                    everything, or erase everything. Originals never alter (Canon Rule 1).
                </p>
            </div>

            {busy && (
                <div className="paper-card p-12 text-center" data-testid="gdpr-busy">
                    <Loader2 className="w-7 h-7 text-stamp-red mx-auto mb-3 animate-spin" />
                    <div className="label-typed text-stamp-red">Loading your file…</div>
                </div>
            )}

            {err && !busy && (
                <div className="paper-card border-2 border-stamp-red p-4 mb-6" data-testid="gdpr-error">
                    <div className="flex items-center gap-2 stamp-red font-heading text-sm tracking-[0.2em] mb-2">
                        <AlertTriangle className="w-4 h-4" /> ERROR
                    </div>
                    <pre className="font-body text-xs text-ink whitespace-pre-wrap">{err}</pre>
                </div>
            )}

            {policy && !busy && (
                <section className="space-y-6" data-testid="gdpr-content">
                    {/* Policy block */}
                    <div className="paper-card p-6" data-testid="gdpr-policy">
                        <div className="flex items-center gap-2 mb-4">
                            <FileText className="w-4 h-4 text-stamp-red" />
                            <div className="font-heading uppercase tracking-[0.2em] text-sm text-ink">
                                {policy.title}
                            </div>
                        </div>

                        <PolicyRow heading="Controller">
                            <div className="font-body text-sm text-ink">{policy.controller?.entity}</div>
                            <div className="font-body text-xs text-ink-soft mt-1">{policy.controller?.contact}</div>
                        </PolicyRow>

                        <PolicyRow heading="What we store">
                            <ul className="list-disc pl-5 space-y-1">
                                {policy.what_we_store?.map((s, i) => (
                                    <li key={i} className="font-body text-sm text-ink">{s}</li>
                                ))}
                            </ul>
                        </PolicyRow>

                        <PolicyRow heading="What we do NOT store">
                            <ul className="list-disc pl-5 space-y-1">
                                {policy.what_we_do_not_store?.map((s, i) => (
                                    <li key={i} className="font-body text-sm text-ink">{s}</li>
                                ))}
                            </ul>
                        </PolicyRow>

                        <PolicyRow heading="Legal basis">
                            <p className="font-body text-sm text-ink">{policy.legal_basis}</p>
                        </PolicyRow>

                        <PolicyRow heading="Retention">
                            <p className="font-body text-sm text-ink">{policy.retention}</p>
                        </PolicyRow>

                        <PolicyRow heading="Your rights">
                            <ul className="list-disc pl-5 space-y-1">
                                {policy.your_rights?.map((s, i) => (
                                    <li key={i} className="font-body text-sm text-ink">{s}</li>
                                ))}
                            </ul>
                        </PolicyRow>

                        <PolicyRow heading="Third parties">
                            <ul className="space-y-1">
                                {policy.third_parties?.map((tp, i) => (
                                    <li key={i} className="font-body text-sm text-ink">
                                        <span className="font-heading text-ink">{tp.name}</span> · {tp.purpose}
                                    </li>
                                ))}
                            </ul>
                        </PolicyRow>

                        <PolicyRow heading="Canon note" last>
                            <p className="font-body text-sm text-ink italic">{policy.canon_note}</p>
                        </PolicyRow>
                    </div>

                    {/* Inventory */}
                    {inventory && (
                        <div className="paper-card p-6" data-testid="gdpr-inventory">
                            <div className="flex items-center justify-between gap-2 mb-4">
                                <div className="flex items-center gap-2">
                                    <Database className="w-4 h-4 text-stamp-red" />
                                    <div className="font-heading uppercase tracking-[0.2em] text-sm text-ink">
                                        Your data on file
                                    </div>
                                </div>
                                <div className="label-typed text-stamp-red">
                                    {inventory.grand_total.toLocaleString()} rows total
                                </div>
                            </div>
                            <div className="font-body text-xs text-ink-soft mb-4">
                                Live counts. As of {new Date(inventory.as_of).toLocaleString()}. Refresh on next visit.
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="border-b border-ink/30">
                                        <tr className="text-left">
                                            <th className="label-typed py-2">Collection</th>
                                            <th className="label-typed py-2">Why</th>
                                            <th className="label-typed py-2 text-right">Rows</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {inventory.rows.map((r) => (
                                            <tr
                                                key={r.collection}
                                                className="border-b border-ink/10"
                                                data-testid={`gdpr-inv-row-${r.collection}`}
                                            >
                                                <td className="py-2 font-body text-ink">{r.label}</td>
                                                <td className="py-2 font-body text-xs text-ink-soft">{r.reason}</td>
                                                <td className="py-2 font-heading text-right text-ink">
                                                    {r.count.toLocaleString()}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Controls */}
                    <div className="paper-card border-2 border-ink/30 p-6" data-testid="gdpr-controls">
                        <div className="font-heading uppercase tracking-[0.2em] text-sm text-ink mb-4">
                            Controls
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                            <button
                                onClick={exportZip}
                                data-testid="gdpr-export-btn"
                                className="font-heading uppercase text-xs tracking-[0.22em] bg-ink text-paper hover:bg-stamp-red px-4 py-3 flex items-center justify-center gap-2"
                            >
                                <Download className="w-3 h-3" />
                                Export all data (ZIP)
                            </button>

                            <button
                                onClick={() => setShowConfirm(true)}
                                disabled={erasing || countdown !== null}
                                data-testid="gdpr-erase-toggle"
                                className="font-heading uppercase text-xs tracking-[0.22em] bg-stamp-red text-paper hover:bg-ink px-4 py-3 disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                <Trash2 className="w-3 h-3" />
                                Erase everything
                            </button>
                        </div>

                        <p className="font-body text-xs text-ink-soft mt-4 max-w-2xl">
                            Export is non-destructive — you can run it any time. Erase removes every
                            row tied to your user_id <em>plus</em> the account record itself. After erase you'll be
                            signed out and your token will not validate. Originals are deleted, not altered (Rule 1
                            applies to <em>filing</em> — erasure is not a correction).
                        </p>
                    </div>

                    {/* Erase confirm + countdown */}
                    {showConfirm && !eraseReport && (
                        <div className="paper-card border-2 border-stamp-red p-6" data-testid="gdpr-confirm-panel">
                            <div className="flex items-center gap-2 stamp-red font-heading text-sm tracking-[0.2em] mb-3">
                                <ShieldAlert className="w-4 h-4" /> FINAL CONFIRMATION
                            </div>
                            <p className="font-body text-sm text-ink mb-4">
                                Type <code className="bg-paper-aged px-2 py-0.5 font-heading text-stamp-red">ERASE</code>{" "}
                                below to start the 5-second countdown. The button stays disabled until the word matches
                                exactly.
                            </p>
                            <input
                                type="text"
                                value={confirmText}
                                onChange={(e) => setConfirmText(e.target.value)}
                                placeholder="Type ERASE"
                                disabled={countdown !== null || erasing}
                                data-testid="gdpr-confirm-input"
                                className="font-heading uppercase tracking-[0.15em] w-full px-3 py-2 border border-stamp-red bg-paper text-ink mb-4 disabled:opacity-50"
                            />
                            <div className="flex flex-wrap items-center gap-3">
                                <button
                                    onClick={beginErase}
                                    disabled={confirmText !== "ERASE" || countdown !== null || erasing}
                                    data-testid="gdpr-confirm-btn"
                                    className="font-heading uppercase text-xs tracking-[0.22em] bg-stamp-red text-paper hover:bg-ink px-4 py-3 disabled:opacity-50 flex items-center gap-2"
                                >
                                    <Trash2 className="w-3 h-3" />
                                    {countdown !== null ? `Erasing in ${countdown}…` : (erasing ? "Erasing…" : "Begin erasure")}
                                </button>
                                <button
                                    onClick={() => { setShowConfirm(false); setConfirmText(""); setCountdown(null); }}
                                    disabled={countdown !== null || erasing}
                                    data-testid="gdpr-cancel-btn"
                                    className="font-heading uppercase text-xs tracking-[0.22em] border border-ink text-ink hover:bg-ink hover:text-paper px-4 py-3 disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                            </div>
                            {countdown !== null && countdown > 0 && (
                                <div className="mt-5 text-center" data-testid="gdpr-countdown">
                                    <div className="font-heading text-stamp-red text-7xl tracking-tight">
                                        {countdown}
                                    </div>
                                    <div className="label-typed text-stamp-red mt-1">
                                        Tap Cancel to abort
                                    </div>
                                </div>
                            )}
                            {eraseError && (
                                <div className="mt-4 paper-card border border-stamp-red p-3" data-testid="gdpr-erase-error">
                                    <pre className="font-body text-xs text-ink whitespace-pre-wrap">{eraseError}</pre>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Erase complete report */}
                    {eraseReport && (
                        <div className="paper-card border-2 border-stamp-green p-6" data-testid="gdpr-erase-report">
                            <div className="flex items-center gap-2 mb-3">
                                <Trash2 className="w-4 h-4 text-stamp-green" />
                                <div className="font-heading uppercase tracking-[0.2em] text-sm text-stamp-green">
                                    File closed
                                </div>
                            </div>
                            <div className="font-body text-sm text-ink mb-3">
                                {eraseReport.total_deleted?.toLocaleString() || 0} rows erased across{" "}
                                {Object.keys(eraseReport.deleted || {}).length} collections. Signing you out…
                            </div>
                            <div className="font-body text-[10px] text-ink-soft mb-3">
                                <Loader2 className="w-3 h-3 inline animate-spin mr-1" />
                                Redirecting to login
                            </div>
                            <details className="font-body text-xs text-ink-soft">
                                <summary className="cursor-pointer">Per-collection breakdown</summary>
                                <pre className="mt-2 whitespace-pre-wrap break-all">
{JSON.stringify(eraseReport.deleted, null, 2)}
                                </pre>
                            </details>
                        </div>
                    )}

                    <div className="text-center pencil text-base text-ink-soft mt-2">
                        <a
                            href="https://ico.org.uk"
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 hover:text-stamp-red"
                        >
                            Lodge a complaint with the UK ICO <ExternalLink className="w-3 h-3" />
                        </a>
                    </div>
                </section>
            )}
        </Shell>
    );
}

function PolicyRow({ heading, children, last }) {
    return (
        <div className={`py-3 ${last ? "" : "border-b border-dashed border-ink/15"}`}>
            <div className="label-typed tw-prefix mb-2">{heading}</div>
            {children}
        </div>
    );
}
